import type {
	Distribution,
	MixedItemDist,
	MixedItemTag,
	PracticeTestExam,
} from "@memorang/types";
/* -----------------Types--------------- */
import type {
	CumulativeSummaryResponse,
	FetchMasteryOvertimeResponse,
} from "./../features/dashboard/types/index";
import type { CurrentView, MasteryTagProgressData } from "./content/types";

import { isScreenshotMode, skillIconMap } from "@constants/common";
import { mockedPracticeTestSummary } from "@constants/mocked-data";
import {
	type Skill,
	getCompositeScoreAndLevel,
} from "@features/report/helpers/points-normalization";
/* -----------------Helpers & Hooks--------------- */
import { groupTagsByType } from "@helpers/content/tag";
import type { AppTheme } from "@hooks/useAppTheme";
import { formatCount, formatNumber } from "@memorang/helpers";
import type { TFunction } from "i18next";
import { sortedMasteryLevels } from "./content/distribution";
import { getParentMetadata } from "./content/metadata";
import { getMappedExams } from "./exams";
import { randomizeDistribution } from "./mocked-helpers";
import { smartPluralize } from "./smartPluralize";

type AreaChartData = {
	date: string;
	masteryCount: number;
	timeTaken?: number;
};

export type ExamCardData = {
	title: string;
	subtitle: string;
	avgScore: number;
	list: {
		title: string;
		description: string;
		status: string;
		numericId: number;
		cta: string;
		id: string;
		score: number;
	}[];
};

export type SummaryCardData = {
	title: string;
	subtitle: string;
	tooltip: string;
	data: {
		name: string;
		list: {
			icon: string;
			title: string;
			description: string;
			chipLabel: string;
			key?: string;
			tooltip?: string;
		}[];
	}[];
};

const sortByDate = (
	items: {
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		x: any;
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		y: any;
	}[],
) => items.sort((a, b) => a.x - b.x);

const setDate = (date: string, hasDate?: boolean) => {
	const dateArr = date.split("-");
	const dateObj = new Date(
		Number.parseInt(dateArr[0], 10),
		Number.parseInt(dateArr[1], 10) - 1,
		hasDate ? Number.parseInt(dateArr[2], 10) : 1,
	);
	return dateObj;
};
const transformDateAndSort = (items: AreaChartData[]) => {
	const result = [...items].map((item) => ({
		x: setDate(item.date),
		y: item.masteryCount,
	}));
	return sortByDate(result);
};

const transformDateAndAvgTime = (items: number[]) => {
	const result = [...items].map((item, index) => ({
		x: index,
		y: item,
	}));
	return sortByDate(result);
};

type ItemTypeTag = {
	title: string;
	numItems: number;
	distribution: Distribution;
	score: number;
};

type ItemTypeTags = Record<string, ItemTypeTag>;

export const getItemTypeTags = (
	tags: MixedItemTag[],
	t: TFunction,
): ItemTypeTags => {
	const allItems = tags.flatMap((tag) => tag.items);

	const groupedItemsByVariant = allItems.reduce<Record<string, MixedItemTag[]>>(
		(acc, item) => {
			if (!item?.variant) {
				return acc;
			}

			acc[item.variant] = acc[item.variant] || [];
			acc[item.variant].push({
				...item,
				title: t(`session.itemTypes.${item.variant}`),
				id: item.id.toString(),
				distributionKey: item.distribution,
				score: item.stats?.score,
				distribution: {
					...sortedMasteryLevels.reduce((acc, item) => {
						acc[item.id] = 0;
						return acc;
					}, {} as Distribution),
					none: 0,
				},
			});

			return acc;
		},
		{},
	);

	return Object.entries(groupedItemsByVariant).reduce<ItemTypeTags>(
		(acc, [key, items]) => {
			acc[key] = {
				title: key,
				numItems: items.length,
				score: items.reduce((a, c) => a + (c?.score || 0), 0) / items.length,
				distribution: items.reduce<Distribution>(
					(dist, item) => {
						const key = item.distributionKey as keyof Distribution;
						dist[key]++;
						return dist;
					},
					{
						...sortedMasteryLevels.reduce((acc, item) => {
							acc[item.id] = 0;
							return acc;
						}, {} as Distribution),
						none: 0,
					},
				),
			};
			return acc;
		},
		{},
	);
};

export const getItemTypeProgressData = (tags: MixedItemTag[], t: TFunction) => {
	const itemTypeTags = getItemTypeTags(tags, t);

	const data = {
		title: t("progress.itemTypeProgress.title"),
		subtitle: t("progress.itemTypeProgress.subtitle", {
			count: Object.keys(itemTypeTags).length,
			item: smartPluralize("itemType", Object.keys(itemTypeTags).length),
		}),
		tooltip: t("progress.itemTypeProgress.tooltip", {
			count: Object.keys(itemTypeTags).length,
			type: smartPluralize("itemType", Object.keys(itemTypeTags).length),
		}),
		items: Object.values(itemTypeTags).map((item) => {
			return {
				title: t(`session.itemTypes.${item.title}`),
				distribution: item.distribution,
				score: Math.round(item.score || 0),
				rightText: t("progress.tagProgress.itemCount", {
					count: item.numItems,
					item: smartPluralize("item", item.numItems),
				}),
			};
		}),
	};

	return data;
};
const getMasteryTagsProgressData = ({
	currentView,
	tags,
	numItems,
	freeTagIds = [],
	hasAccess,
	t,
	theme,
}: {
	currentView: CurrentView;
	tags: MixedItemTag[];
	freeTagIds?: string[];
	hasAccess?: boolean;
	numItems: number;
	t: TFunction;
	theme: AppTheme;
}): MasteryTagProgressData[] => {
	const groupedTagsByType = groupTagsByType(
		tags,
		currentView,
		freeTagIds,
		hasAccess,
	);
	const data = Object.keys(groupedTagsByType)
		.filter((item) => {
			return groupedTagsByType[item].length > 0;
		})
		.map((key) => {
			const skillCount = groupedTagsByType[key].length;
			return {
				title: "Topic mastery",
				tagType: key.toLowerCase(),
				tooltip: t("progress.tagProgress.tooltip", {
					type: smartPluralize("skill", skillCount),
					count: skillCount,
					item: smartPluralize("item", numItems),
					itemCount: numItems,
				}),
				subtitle: t("progress.tagProgress.subtitle", {
					numItems,
					type: smartPluralize("skill", skillCount),
					count: skillCount,
					item: smartPluralize("item", numItems),
				}),
				items: groupedTagsByType[key].map((tag) => {
					const proficiency = getCompositeScoreAndLevel(
						[
							{
								skill: tag.label as Skill,
								numPointsEarned: tag.numPointsEarned || 0,
								numTotalPoints: tag.numTotalPoints || 0,
							},
						],
						t,
						theme,
					);
					const masteryLevel = sortedMasteryLevels.find(
						(item) => item.id === proficiency.compositeLevel,
					);
					const description = `${t("common.estimatedProficiency")} - ${t(masteryLevel?.alias.title || "")}`;

					const colorScale = [
						proficiency.compositeColor,
						theme.colors.progress.none,
					];
					return {
						title: (tag as { label?: string }).label ?? tag.title,
						icon: skillIconMap[(tag as { label?: string }).label ?? tag.title],
						distribution: isScreenshotMode
							? randomizeDistribution(tag.distribution)
							: tag.distribution,
						score: proficiency.compositeScaledScore,
						rightText: t("progress.tagProgress.itemCount", {
							count: tag.numItems || 0,
							item: smartPluralize("item", tag.numItems || 0),
						}),
						description,
						colorScale,
					};
				}),
			};
		});

	return data;
};

const getProgressPageSummaryCardData = (
	response: MixedItemDist,
	numTotalItems: number,
	numAttemptedItems: number,
	t: TFunction,
) => {
	const { distribution } = getParentMetadata(response.contents, numTotalItems);
	const numCompletion =
		Object.values(distribution).reduce((a, b) => a + b, 0) - distribution.none;

	if (!numCompletion) {
		return undefined;
	}

	const masteryList = sortedMasteryLevels.map((item) => {
		const count = distribution[item.id];
		const tooltipKeyMap: Record<string, string> = {
			high: "progress.mastery.highTooltip",
			medium: "progress.mastery.mediumTooltip",
			low: "progress.mastery.lowTooltip",
		};
		const tooltipKey = tooltipKeyMap[item.id];
		const tooltip = tooltipKey
			? count === 0
				? t("progress.mastery.zeroTooltip", {
						label: item.alias.title.toLocaleLowerCase(),
						total: formatNumber(numCompletion),
					})
				: t(tooltipKey, {
						count,
						total: formatNumber(numCompletion),
						item: smartPluralize("item", numCompletion),
					})
			: undefined;
		return {
			icon: item.icon,
			title: t(item.alias.title),
			iconColor: item.lightColor,
			description: t("progress.mastery.count", {
				count: distribution[item.id],
				item: smartPluralize("item", distribution[item.id]),
			}),
			chipLabel: `${Math.round((distribution[item.id] / numTotalItems) * 100)}%`,
			key: item.id,
			tooltip,
		};
	});

	const stats = [
		{
			icon: "checkbox-multiple-outline",
			title: t("progress.stats.completion"),
			description: t("progress.stats.completionCount", {
				completed: numCompletion,
				total: numTotalItems,
				item: smartPluralize("item", numTotalItems),
			}),
			chipLabel: `${Math.round((numCompletion / numTotalItems) * 100)}%`,
			tooltip: t("progress.stats.completionTooltip", {
				completed: numCompletion,
				total: numTotalItems,
				item: smartPluralize("item", numTotalItems),
			}),
		},
	];

	const data: SummaryCardData = {
		title: t("progress.summary.title"),
		subtitle: t("progress.summary.subtitle", {
			count: numAttemptedItems,
			item: smartPluralize("item", numAttemptedItems),
		}),
		tooltip: t("progress.summary.tooltip", {
			count: numAttemptedItems,
			item: smartPluralize("item", numAttemptedItems),
		}),
		data: [
			{
				name: t("progress.summary.mastery"),
				list: masteryList,
			},
			{
				name: t("progress.summary.quickStats"),
				list: stats,
			},
		],
	};
	return data;
};
const getCumulativeProgressData = ({
	cumulativeSummaryStats: { cumulative },
}: CumulativeSummaryResponse) => {
	const {
		speed,
		accuracy,
		completion: { distCompleted, distCount, completionPercentage },
	} = cumulative[0];
	const items = [
		{
			title: "Completion",
			overline: `${distCompleted} / ${distCount} items`,
			rightText: `${completionPercentage}%`,
			icon: "text-box-check-outline",
		},
		{
			title: "Accuracy",
			overline: "Percent Correct",
			rightText: `${accuracy}%`,
			icon: "bullseye-arrow",
		},
		{
			title: "Speed",
			overline: "Avg Per Item",
			rightText: `${speed}s / item`,
			icon: "timer-outline",
		},
	];
	const data = {
		title: "Cumulative Summary",
		subtitle: `Based on ${formatNumber(distCount)} items`,
		items,
	};
	return data;
};

const _getMasteryChartProgressData = (
	numItems: number,
	masteryOvertime?: FetchMasteryOvertimeResponse,
) => {
	if (!masteryOvertime) {
		return null;
	}
	const {
		masteryOverTime: { mastery },
	} = masteryOvertime;
	const numMastered = mastery.reduce((a, c) => a + c.masteryCount, 0);

	const chartData = transformDateAndSort(mastery);

	const xTickValues = chartData.map((item) => item.x);
	const yTickValues = [
		...new Set(chartData.filter((item) => item.y).map((item) => item.y)),
	];
	const data = {
		subtitle: `Based on ${formatNumber(numItems)} items`,
		title: "Items Mastered",
		stats: [
			{
				title: "Total Mastered",
				rightText: `${numMastered}`,
				icon: "check-outline",
			},
		],
		chartData: {
			data: chartData,
			name: "mastery-area",
			fillColor: "#66bb68",
			strokeColor: "#66bb68",
			xTickValues: xTickValues,
			yTickValues: yTickValues,
			itemsCount: numItems,
		},
	};
	return data;
};

const _getSpeedChartProgressData = (
	numItems: number,
	mixedItemDist: MixedItemDist,
	masteryOvertime?: FetchMasteryOvertimeResponse,
) => {
	if (!masteryOvertime) {
		return null;
	}
	const {
		masteryOverTime: { speed },
	} = masteryOvertime;

	const chartData = transformDateAndAvgTime(speed);

	const filteredChartData = chartData.filter((item) => item.y);
	const avgSpeed =
		chartData.reduce((a, b) => a + b.y, 0) / filteredChartData.length;
	const {
		distribution: { high, low, medium },
	} = getParentMetadata(mixedItemDist.contents, numItems);

	const maxItemCount = Math.ceil(avgSpeed + 1);
	const badgeValue = `${Math.ceil(avgSpeed)} ${
		Math.ceil(avgSpeed) === 1 ? "sec" : "secs"
	} / item`;

	const answeredItems = high + low + medium;

	const data = {
		subtitle: `Based on ${answeredItems} items`,
		title: "Speed",
		stats: [
			{
				title: "Average Speed",
				rightText: badgeValue,
				icon: "timer-outline",
			},
		],
		chartData: {
			data: chartData,
			name: "speed-area",
			fillColor: "#d66565",
			strokeColor: "#d66565",
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			xTickValues: chartData.map<any>((item) => item.x),
			itemsCount: maxItemCount,
		},
	};
	return data;
};

const getNeedsImprovementProgressData = (
	mixedItemDist: MixedItemDist,
	t: TFunction,
	theme: AppTheme,
) => {
	const { tags, freeTagIds } = mixedItemDist;
	const groupedTagsByType = groupTagsByType(tags, "Progress", freeTagIds);
	const allTags = Object.keys(groupedTagsByType).flatMap(
		(key) => groupedTagsByType[key],
	);
	const lowScoredTags = allTags.filter((item) =>
		Object.entries(item.distribution).some(
			([key, value]) => key !== "none" && value > 0,
		),
	);

	const sortedLowScoredTags = lowScoredTags?.map((tag) => ({
		...tag,
		distribution: tag.distribution,
	}));

	if (sortedLowScoredTags.length) {
		const data = {
			title: t("progress.needsImprovement.title"),
			subtitle: t("progress.needsImprovement.subtitle", {
				type: t("common.skill"),
			}),
			icon: "chart-gantt",
			items: sortedLowScoredTags.map((tag) => {
				const proficiencyLevel = getCompositeScoreAndLevel(
					[
						{
							skill: tag.label as Skill,
							numPointsEarned: tag.numPointsEarned || 0,
							numTotalPoints: tag.numTotalPoints || 0,
						},
					],
					t,
					theme,
				);
				const colorScale = [
					proficiencyLevel.compositeColor,
					theme.colors.progress.none,
				];
				return {
					title: (tag as { label?: string }).label ?? tag.title,
					icon: skillIconMap[(tag as { label?: string }).label ?? tag.title],
					distribution: tag.distribution,
					rightText: t("progress.tagProgress.itemCount", {
						count: tag.numItems || 0,
						item: smartPluralize("item", tag.numItems || 0),
					}),
					id: tag.id,
					colorScale,
					score: proficiencyLevel.compositeScaledScore,
				};
			}),
		};
		return data;
	}
	return null;
};

const getPracticeTestsCardsData = (exams?: PracticeTestExam[]) => {
	const filteredExams = exams?.filter(({ status }) => status === "COMPLETE");

	if (!filteredExams?.length) {
		return undefined;
	}
	const avgScore =
		getMappedExams(filteredExams).reduce((a, c) => a + c.score, 0) /
		filteredExams.length;

	const examsCardData: ExamCardData = {
		title: "Practice Tests",
		subtitle: `Based on ${formatCount(
			"completed assessment",
			filteredExams.length,
			true,
		)}`,
		avgScore: Math.round(avgScore),
		list: filteredExams?.length ? getMappedExams(filteredExams) : [],
	};
	return examsCardData;
};

export const hardcodedPracticeTestExams = (
	_examName: string,
	exams: PracticeTestExam[],
) => ({
	title: "Practice Tests",
	subtitle: `Based on ${exams.length} completed diagnostics`,
	avgScore: 85,
	list: exams.slice(0, 3).map((exam) => ({
		title: exam.title,
		description: `${exam.numItems} items • May 9, 2023 • 50:00`,
		status: "COMPLETE",
		numericId: 15277,
		cta: "View Report",
		id: "B",
		score: Math.floor(Math.random() * 11) + 90,
	})),
});
const getProgressCardsData = (
	data: MixedItemDist,
	currentView: CurrentView,
	hasAccess: boolean,
	t: TFunction,
	theme: AppTheme,
	examName?: string,
) => {
	const numItemsAttempted = data.contents.reduce(
		(a, c) =>
			a +
			Object.keys(c.distribution)
				.filter((key) => key !== "none")
				.reduce((b, d) => b + c.distribution[d as keyof Distribution], 0),
		0,
	);
	const numItems = data.contents.reduce((a, c) => a + c.totalCount, 0);
	const masteryTagsProgressData = getMasteryTagsProgressData({
		currentView,
		tags: data.tags,
		numItems: numItemsAttempted,
		hasAccess,
		freeTagIds: data.freeTagIds,
		t,
		theme,
	});

	const needsImprovementProgressData = getNeedsImprovementProgressData(
		data,
		t,
		theme,
	);

	const showEmptyState = needsImprovementProgressData == null;

	const itemTypeProgressData = getItemTypeProgressData(data.tags, t);

	const practiceTestsCardsData = isScreenshotMode
		? hardcodedPracticeTestExams(examName!, data.exams!)
		: getPracticeTestsCardsData(data.exams);
	const progressPageSummaryCardData = isScreenshotMode
		? mockedPracticeTestSummary
		: getProgressPageSummaryCardData(data, numItems, numItemsAttempted, t);
	return {
		masteryTagsProgressData,
		needsImprovementProgressData,
		showEmptyState,
		practiceTestsCardsData,
		progressPageSummaryCardData,
		itemTypeProgressData,
		numItemsAttempted,
	};
};

export {
	getProgressCardsData,
	getCumulativeProgressData,
	getMasteryTagsProgressData,
};
